if (!customElements.get('collection-tabs')) {
  customElements.define(
    'collection-tabs',
    class CollectionTabs extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('keydown', this.onKeydown.bind(this));
      }

      get tabs() {
        return Array.from(this.querySelectorAll('[role="tab"]'));
      }

      get panels() {
        return Array.from(this.querySelectorAll('[role="tabpanel"]'));
      }

      onClick(event) {
        const tab = event.target.closest('[role="tab"]');
        if (tab) {
          this.selectTab(tab);
          return;
        }

        const filterToggle = event.target.closest('[data-filter-toggle]');
        if (filterToggle) {
          this.toggleFilterPanel(filterToggle);
          return;
        }

        const sizeChip = event.target.closest('[data-filter-size-toggle]');
        if (sizeChip) {
          this.toggleSize(sizeChip);
          return;
        }

        const colorChip = event.target.closest('[data-filter-color-toggle]');
        if (colorChip) {
          this.selectColor(colorChip);
          return;
        }

        const clearButton = event.target.closest('[data-filter-clear]');
        if (clearButton) {
          this.clearFilters(clearButton.closest('[role="tabpanel"]'));
        }
      }

      onChange(event) {
        if (!event.target.matches('[data-filter-sort]')) return;
        const panel = event.target.closest('[role="tabpanel"]');
        this.sortPanel(panel, event.target.value);
      }

      selectTab(selectedTab) {
        this.tabs.forEach((tab) => {
          const isSelected = tab === selectedTab;
          tab.setAttribute('aria-selected', isSelected);
          tab.setAttribute('tabindex', isSelected ? '0' : '-1');
          tab.classList.toggle('custom-featured-collection__tab--active', isSelected);
        });

        this.panels.forEach((panel) => {
          panel.hidden = panel.id !== selectedTab.getAttribute('aria-controls');
        });
      }

      onKeydown(event) {
        const currentTab = event.target.closest('[role="tab"]');
        if (!currentTab) return;

        const moves = { ArrowRight: 1, ArrowLeft: -1 };
        if (!(event.key in moves) && event.key !== 'Home' && event.key !== 'End') return;

        event.preventDefault();
        const tabs = this.tabs;
        const currentIndex = tabs.indexOf(currentTab);
        let newIndex = currentIndex;

        if (event.key === 'Home') newIndex = 0;
        else if (event.key === 'End') newIndex = tabs.length - 1;
        else newIndex = (currentIndex + moves[event.key] + tabs.length) % tabs.length;

        tabs[newIndex].focus();
        this.selectTab(tabs[newIndex]);
      }

      toggleFilterPanel(button) {
        const panel = this.querySelector(`#${button.getAttribute('aria-controls')}`);
        if (!panel) return;
        const isOpen = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!isOpen));
        panel.hidden = isOpen;
      }

      toggleSize(chip) {
        chip.classList.toggle('is-active');
        this.applyFilters(chip.closest('[role="tabpanel"]'));
      }

      selectColor(chip) {
        const panel = chip.closest('[role="tabpanel"]');
        panel.querySelectorAll('[data-filter-color-toggle]').forEach((item) => item.classList.remove('is-active'));
        chip.classList.add('is-active');
        this.applyFilters(panel);
      }

      clearFilters(panel) {
        if (!panel) return;

        panel.querySelectorAll('[data-filter-size-toggle]').forEach((chip) => chip.classList.remove('is-active'));
        panel.querySelectorAll('[data-filter-color-toggle]').forEach((chip) => chip.classList.remove('is-active'));
        const allColorChip = panel.querySelector('[data-filter-color-toggle][data-value=""]');
        if (allColorChip) allColorChip.classList.add('is-active');

        const sortSelect = panel.querySelector('[data-filter-sort]');
        if (sortSelect) sortSelect.value = 'featured';
        this.sortPanel(panel, 'featured');

        this.applyFilters(panel);
      }

      applyFilters(panel) {
        if (!panel) return;

        const activeSizes = new Set(
          Array.from(panel.querySelectorAll('[data-filter-size-toggle].is-active')).map((chip) => chip.dataset.value)
        );
        const activeColorChip = panel.querySelector('[data-filter-color-toggle].is-active');
        const activeColor = activeColorChip ? activeColorChip.dataset.value || '' : '';
        const hasSizeFilter = activeSizes.size > 0;
        const hasColorFilter = activeColor !== '';

        const cards = panel.querySelectorAll('[data-filter-sizes]');
        cards.forEach((card) => {
          const cardSizes = (card.dataset.filterSizes || '').split('|').filter(Boolean);
          const cardColor = card.dataset.filterColor || '';

          const matchesSize = !hasSizeFilter || cardSizes.some((size) => activeSizes.has(size));
          const matchesColor = !hasColorFilter || cardColor === activeColor;
          const visible = matchesSize && matchesColor;

          card.style.display = visible ? '' : 'none';
        });

        const activeFilterCount = activeSizes.size + (hasColorFilter ? 1 : 0);
        const countBadge = panel.querySelector('[data-filter-count]');
        if (countBadge) {
          countBadge.textContent = String(activeFilterCount);
          countBadge.style.visibility = activeFilterCount === 0 ? 'hidden' : 'visible';
        }

        const clearButton = panel.querySelector('[data-filter-clear]');
        if (clearButton) clearButton.style.visibility = activeFilterCount === 0 ? 'hidden' : 'visible';
      }

      sortPanel(panel, sortValue) {
        if (!panel) return;
        const list = panel.querySelector('[role="list"]');
        if (!list) return;
        const cards = Array.from(list.children);
        if (!cards.length) return;

        if (sortValue === 'price-asc' || sortValue === 'price-desc') {
          const direction = sortValue === 'price-asc' ? 1 : -1;
          cards.sort((a, b) => direction * (Number(a.dataset.filterPrice) - Number(b.dataset.filterPrice)));
        } else {
          cards.sort((a, b) => Number(a.dataset.originalIndex) - Number(b.dataset.originalIndex));
        }

        cards.forEach((card) => list.appendChild(card));
      }
    }
  );
}
